import {
  Button,
  Checkbox,
  Select,
  SelectOption,
  Spacer,
  Stack,
  TextBody,
  TextCaption,
  TextTitle,
} from '@pypestream/design-system';
import {
  createDefaultWidgetAppConfig,
  fetchWidgetAppDesign,
  type WidgetAppConfig,
} from '@pypestream/pype/customization';
import { FC, useEffect, useMemo, useState } from 'react';

import {
  GenericModal,
  GenericModalProps,
  useGenericModal,
} from '../components';
import { buildEngagementApiUrl, type Channel } from '../utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import { sendUserMessage } from '../xstate/app.xstate-utils';
import {
  copyLegacyStyling,
  copyPypeNextDesign,
  diffLegacyStyling,
  diffMessages,
  messagesAvailability,
  stylingAvailability,
  type SettingChange,
} from './copy-channel-settings';
import { toAutorespondersInput, toPlatformMessages } from './default-messages';
import { mutateWidgetAppDesign } from './pype-designer/mutate-widget-app-design';
import { withResolvedDefaults } from './pype-designer/pype-designer-utils';
import { resolvePypeNextAccess } from './pype-designer/resolve-pype-next-access';

import './copy-channel-settings-modal.scss';
import { singleSelectValue } from '../utils/single-select-value';

const envLabel = (env: Channel['env']) => (env === 'live' ? 'Live' : 'Sandbox');
const channelLabel = (c: Channel) => `${c.name} (${envLabel(c.env)})`;

const loadDesign = async (channel: Channel): Promise<WidgetAppConfig | null> =>
  fetchWidgetAppDesign({
    appId: channel.id,
    engagementApiUrl: buildEngagementApiUrl(
      window.location.origin,
      channel.env
    ),
  });

/** Resolve on the event's `cb`, reject on its `errorCb`. */
const awaitEvent = (
  send: (callbacks: { cb: () => void; errorCb: () => void }) => void
) =>
  new Promise<void>((resolve, reject) =>
    send({ cb: resolve, errorCb: () => reject(new Error('save failed')) })
  );

const ChangeList: FC<{ title: string; changes: SettingChange[] }> = ({
  title,
  changes,
}) => (
  <div className="c-copy-settings__changes">
    <TextBody size="small">{title}</TextBody>
    {changes.length === 0 ? (
      <TextCaption variant="tertiary">
        Already the same — nothing changes.
      </TextCaption>
    ) : (
      <ul>
        {changes.map((c) => (
          <li key={c.label}>
            <span className="c-copy-settings__label">{c.label}</span>
            <span className="c-copy-settings__from">{c.from}</span>
            <span aria-hidden="true">→</span>
            <span className="c-copy-settings__to">{c.to}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

type CopySettingsModalProps = GenericModalProps & {
  source: Channel | null;
  channels: Channel[];
};

/**
 * "Copy settings…" — copy a channel's default messages and/or styling onto
 * another channel of the same solution. See `copy-channel-settings.ts` for
 * what moves and what never does.
 */
const CopySettingsModal: FC<CopySettingsModalProps> = ({
  source,
  channels,
  modalRef,
  onClose: onCloseProp,
}) => {
  const { defaultMessagesPypes, featureFlags, projectId } =
    useProStudioCtxSelector((ctx) => ({
      defaultMessagesPypes: ctx.defaultMessagesPypes,
      featureFlags: ctx.featureFlags,
      projectId: ctx.project?.id,
    }));
  const pypeNext = resolvePypeNextAccess(featureFlags) === 'allowed';

  const targets = useMemo(
    () => channels.filter((c) => source && c.id !== source.id),
    [channels, source]
  );
  const [targetId, setTargetId] = useState<string>('');
  const [wantMessages, setWantMessages] = useState(true);
  const [wantStyling, setWantStyling] = useState(true);
  const [step, setStep] = useState<'choose' | 'confirm'>('choose');
  const [saving, setSaving] = useState(false);
  const [designs, setDesigns] = useState<{
    source: WidgetAppConfig | null;
    target: WidgetAppConfig | null;
  } | null>(null);

  const target = targets.find((c) => c.id === targetId) ?? null;

  useEffect(() => {
    if (defaultMessagesPypes == null) {
      sendProStudioEvent({ type: 'loadDefaultMessages' });
    }
  }, [defaultMessagesPypes]);

  const pypeOf = (channel: Channel) =>
    defaultMessagesPypes?.[channel.env]?.find((p) => p.id === channel.pypeId);

  const messagesState =
    source && target
      ? messagesAvailability(source, target, (pypeId) =>
          [
            ...(defaultMessagesPypes?.live ?? []),
            ...(defaultMessagesPypes?.sandbox ?? []),
          ].some((p) => p.id === pypeId)
        )
      : null;
  const stylingState =
    source && target ? stylingAvailability(source, target) : null;

  const doMessages = !!messagesState?.available && wantMessages;
  const doStyling = !!stylingState?.available && wantStyling;

  const reset = () => {
    setTargetId('');
    setWantMessages(true);
    setWantStyling(true);
    setStep('choose');
    setSaving(false);
    setDesigns(null);
  };
  const onClose = () => {
    reset();
    onCloseProp?.();
  };

  const review = async () => {
    setStep('confirm');
    if (doStyling && pypeNext && source && target) {
      const [s, t] = await Promise.all([
        loadDesign(source).catch(() => null),
        loadDesign(target).catch(() => null),
      ]);
      setDesigns({ source: s, target: t });
    }
  };

  const messageChanges =
    doMessages && source && target && pypeOf(source) && pypeOf(target)
      ? diffMessages(
          toPlatformMessages(pypeOf(source)!),
          toPlatformMessages(pypeOf(target)!)
        )
      : [];
  const stylingChanges =
    doStyling && source && target ? diffLegacyStyling(source, target) : [];

  const apply = async () => {
    if (!source || !target || !projectId) return;
    setSaving(true);
    const done: string[] = [];
    const failed: string[] = [];

    if (doMessages) {
      const sourcePype = pypeOf(source);
      try {
        if (!sourcePype) throw new Error('no source messages');
        await awaitEvent((callbacks) =>
          sendProStudioEvent({
            type: 'updateDefaultMessages',
            payload: {
              id: target.pypeId,
              env: target.env,
              autoresponders: toAutorespondersInput(
                toPlatformMessages(sourcePype)
              ),
              ...callbacks,
            },
          })
        );
        done.push('default messages');
      } catch {
        failed.push('default messages');
      }
    }

    if (doStyling) {
      try {
        await awaitEvent((callbacks) =>
          sendProStudioEvent({
            type: 'updateChannel',
            payload: {
              ...target,
              ...copyLegacyStyling(source, target),
              id: target.id,
              ...callbacks,
            },
          })
        );
        done.push('styling & CSS');
      } catch {
        failed.push('styling & CSS');
      }

      if (pypeNext && designs?.source) {
        try {
          await mutateWidgetAppDesign({
            appId: target.id,
            env: target.env,
            projectId,
            config: copyPypeNextDesign(
              designs.source,
              designs.target ??
                withResolvedDefaults(createDefaultWidgetAppConfig())
            ),
          });
          done.push('Pype Next design');
        } catch {
          failed.push('Pype Next design');
        }
      }
    }

    setSaving(false);
    if (failed.length === 0) {
      sendUserMessage({
        type: 'success',
        text: `Copied ${done.join(', ')} to ${target.name}.`,
        autoClose: 3000,
      });
      onClose();
    } else {
      sendUserMessage({
        type: 'error',
        text:
          (done.length ? `Copied ${done.join(', ')}, but ` : '') +
          `couldn't copy ${failed.join(', ')} to ${target.name}.`,
        autoClose: 6000,
      });
    }
  };

  if (!source) return null;

  return (
    <GenericModal
      test-id="bobcat-copy-settings-modal"
      modalRef={modalRef}
      onClose={onClose}
      onError={() => setSaving(false)}
      hideCloseBtn={saving}
      stayOnClickOutside={saving}
    >
      <Stack slot="header" direction="column" gutter="2xsmall">
        <TextTitle size="small">Copy settings</TextTitle>
        <TextBody variant="secondary">From {channelLabel(source)}</TextBody>
      </Stack>

      <div className="c-copy-settings">
        {step === 'choose' ? (
          <>
            <TextBody size="small">Copy to</TextBody>
            <Spacer size="2xsmall" />
            <Select
              test-id="bobcat-copy-settings-target"
              variant="outlined"
              size="small"
              value={targetId}
              placeholder="Choose a channel"
              onChange={(e) => setTargetId(singleSelectValue(e.target.value))}
            >
              {targets.map((c) => (
                <SelectOption key={c.id} value={c.id}>
                  {channelLabel(c)}
                </SelectOption>
              ))}
            </Select>
            {targets.length === 0 && (
              <TextCaption variant="tertiary">
                This solution has no other channel to copy to.
              </TextCaption>
            )}

            <Spacer size="medium" />
            <TextBody size="small">What to copy</TextBody>
            <Spacer size="2xsmall" />
            <Checkbox
              test-id="bobcat-copy-settings-messages"
              size="small"
              checked={target ? doMessages : wantMessages}
              disabled={!!messagesState && !messagesState.available}
              onChange={(e) => setWantMessages(e.target.checked)}
            >
              Default messages (offline, busy, technical error)
            </Checkbox>
            {messagesState && !messagesState.available && (
              <TextCaption variant="tertiary">
                {messagesState.reason}
              </TextCaption>
            )}
            <Spacer size="xsmall" />
            <Checkbox
              test-id="bobcat-copy-settings-styling"
              size="small"
              checked={target ? doStyling : wantStyling}
              disabled={!!stylingState && !stylingState.available}
              onChange={(e) => setWantStyling(e.target.checked)}
            >
              Styling &amp; CSS
            </Checkbox>
            {stylingState && !stylingState.available && (
              <TextCaption variant="tertiary">
                {stylingState.reason}
              </TextCaption>
            )}
            <Spacer size="small" />
            <TextCaption variant="tertiary">
              Names, logos, launcher text and connection settings are never
              copied.
            </TextCaption>
          </>
        ) : (
          target && (
            <>
              <TextBody size="small">
                Replace {channelLabel(target)}&apos;s{' '}
                {[doMessages && 'default messages', doStyling && 'styling']
                  .filter(Boolean)
                  .join(' and ')}{' '}
                with {source.name}&apos;s?
              </TextBody>
              <Spacer size="small" />
              {doMessages && (
                <ChangeList title="Default messages" changes={messageChanges} />
              )}
              {doStyling && (
                <ChangeList title="Styling & CSS" changes={stylingChanges} />
              )}
              {doStyling && pypeNext && (
                <TextCaption variant="tertiary">
                  {designs === null
                    ? 'Checking the Pype Next design…'
                    : designs.source
                      ? 'Pype Next design (theme, tokens, part overrides, CSS, layout and images) will also be copied.'
                      : `${source.name} has no Pype Next design saved, so that part is skipped.`}
                </TextCaption>
              )}
            </>
          )
        )}
      </div>

      <Stack slot="footer" justifyContent="end">
        {step === 'choose' ? (
          <>
            <Button variant="ghost" size="large" onClick={onClose}>
              Cancel
            </Button>
            <Button
              test-id="bobcat-copy-settings-review"
              size="large"
              disabled={!target || (!doMessages && !doStyling)}
              onClick={review}
            >
              Review
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              size="large"
              disabled={saving}
              onClick={() => setStep('choose')}
            >
              Back
            </Button>
            <Button
              test-id="bobcat-copy-settings-apply"
              size="large"
              disabled={saving || (doStyling && pypeNext && designs === null)}
              onClick={apply}
            >
              {saving ? 'Copying…' : 'Copy settings'}
            </Button>
          </>
        )}
      </Stack>
    </GenericModal>
  );
};

export const useCopySettingsModal = (props: {
  source: Channel | null;
  channels: Channel[];
}) => {
  const { modalRef, openModal, closeModal } = useGenericModal();
  return {
    openModal,
    copySettingsModal: (
      <CopySettingsModal
        modalRef={modalRef}
        onClose={closeModal}
        source={props.source}
        channels={props.channels}
      />
    ),
  };
};
